import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useQuery, useMutation } from '@apollo/client/react';
import { ViewApplicationDocument, GetQuestionsDocument } from 'graphql/queries.generated';
import { RespondToApplicationDocument } from 'graphql/mutations.generated';
import Image from 'components/Image';
import { ImageOwnerType, ImageSize } from 'app-constants';
import styles from 'pages/MemberApplication.module.css';
import globalStyles from 'Global.module.css';

const MemberApplication: React.FC = () => {
    const { organizationId, organizationMemberId } = useParams<{ organizationId: string; organizationMemberId: string }>();
    const navigate = useNavigate();
    const [error, setError] = useState('');
    const [processing, setProcessing] = useState(false);

    const { data: memberData, loading: memberLoading } = useQuery(ViewApplicationDocument, {
        variables: { organizationMemberId: organizationMemberId! },
        skip: !organizationMemberId,
    });

    const { data: questionsData, loading: questionsLoading } = useQuery(GetQuestionsDocument, {
        variables: { organizationId: organizationId! },
        skip: !organizationId,
    });

    const [respondToApplication] = useMutation(RespondToApplicationDocument);

    const handleDecision = async (approved: boolean) => {
        setError('');
        setProcessing(true);
        
        try {
            await respondToApplication({
                variables: {
                    organizationMemberId: organizationMemberId!,
                    approved,
                },
            });
            
            navigate(`/groups/${organizationId}/settings`);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            setProcessing(false);
        }
    };

    if (memberLoading || questionsLoading) {
        return <div className={styles.container}>Loading...</div>;
    }

    if (!memberData?.organizationMember || !questionsData?.questions) {
        return <div className={styles.container}>Application not found.</div>;
    }

    const member = memberData.organizationMember;
    const questions = questionsData.questions;

    const getAnswerForQuestion = (questionId: string) => {
        return member.answers.find((a: { organizationMembershipQuestionId: string; answer: string }) => a.organizationMembershipQuestionId === questionId)?.answer || '';
    };

    return (
        <div className={globalStyles.shadowBox + ' ' + styles.container}>
            <h1>Member Application</h1>
            
            <div className={styles.profile}>
                <Image 
                    imageOwnerType={ImageOwnerType.Person} 
                    imageSize={ImageSize.Profile} 
                    imageId={member.mainProfile.imageId} 
                    className={styles.profileImage}
                />
                <h2>{member.mainProfile.username}</h2>
                {member.mainProfile.bio && <p className={styles.bio}>{member.mainProfile.bio}</p>}
            </div>

            {questions.length > 0 && (
                <div className={styles.questions}>
                    <h3>Application Answers</h3>
                    {questions.map((question: { organizationMembershipQuestionId: string; question: string }) => (
                        <div key={question.organizationMembershipQuestionId} className={styles.questionAnswer}>
                            <p className={styles.question}>{question.question}</p>
                            <p className={styles.answer}>{getAnswerForQuestion(question.organizationMembershipQuestionId)}</p>
                        </div>
                    ))}
                </div>
            )}

            {error && <p className={styles.error}>{error}</p>}

            <div className={styles.actions}>
                <button 
                    className={globalStyles.submitButton + ' ' + styles.approveButton}
                    onClick={() => handleDecision(true)}
                    disabled={processing}
                >
                    Approve
                </button>
                <button 
                    className={globalStyles.submitButton + ' ' + styles.rejectButton}
                    onClick={() => handleDecision(false)}
                    disabled={processing}
                >
                    Reject
                </button>
            </div>
        </div>
    );
};

export default MemberApplication;